import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './SchoolCommissionReport.css';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const TERM_OPTIONS = [
  { value: 'term1', label: 'Term 1' },
  { value: 'term2', label: 'Term 2' },
  { value: 'term3', label: 'Term 3' },
  { value: 'term4', label: 'Term 4' },
  { value: 'year',  label: 'Full Year' },
];

function SchoolCommissionReport({ onClose }) {
  const [schools, setSchools]                   = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [periodType, setPeriodType]             = useState('term');
  const [selectedTerm, setSelectedTerm]         = useState('term1');
  const [selectedMonth, setSelectedMonth]       = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear]         = useState(new Date().getFullYear());
  const [studentFilter, setStudentFilter]       = useState('all');
  const [reportData, setReportData]             = useState(null);
  const [loadingSchools, setLoadingSchools]     = useState(true);
  const [loadingReport, setLoadingReport]       = useState(false);
  const [franchiseeData, setFranchiseeData]     = useState({});

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user          = auth.currentUser;
        const franchiseeRef = doc(db, 'Franchisees', user.uid);
        const fDoc          = await getDoc(franchiseeRef);
        if (fDoc.exists()) setFranchiseeData(fDoc.data());

        const q    = query(collection(db, 'schools'), where('franchiseeRef', '==', franchiseeRef), where('isActive', '==', true));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.schoolName || '').localeCompare(b.schoolName || ''));
        setSchools(list);
        if (list.length > 0) setSelectedSchoolId(list[0].id);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoadingSchools(false);
      }
    };
    fetchData();
  }, []);

  const getTermForMonth = (termStructure, month0, year) => {
    if (!termStructure?.termDates) return null;
    for (let n = 1; n <= 4; n++) {
      const td = termStructure.termDates['term' + n];
      if (!td?.start || !td?.end) continue;
      const [sm, sd] = td.start.split('-').map(Number);
      const [em, ed] = td.end.split('-').map(Number);
      const termStart  = new Date(year, sm - 1, sd);
      const termEnd    = new Date(year, em - 1, ed);
      const monthStart = new Date(year, month0, 1);
      const monthEnd   = new Date(year, month0 + 1, 0);
      if (monthStart <= termEnd && monthEnd >= termStart) return 'term' + n;
    }
    return null;
  };

  const getPeriodLabel = () => {
    if (periodType === 'month') return MONTH_NAMES[selectedMonth] + ' ' + selectedYear;
    return TERM_OPTIONS.find(t => t.value === selectedTerm)?.label || selectedTerm;
  };

  const generateReport = async () => {
    if (!selectedSchoolId) { alert('Please select a school first.'); return; }
    setLoadingReport(true);
    setReportData(null);
    try {
      const school     = schools.find(s => s.id === selectedSchoolId);
      if (!school) return;
      const commission = school.commission || { type: 'none', value: 0 };

      let termStructure = null;
      if (school.termStructureRef) {
        const tsSnap = await getDoc(school.termStructureRef);
        if (tsSnap.exists()) termStructure = tsSnap.data();
      }

      let effectiveTermKey = null;
      let periodStart      = null;
      let periodEnd        = null;

      if (periodType === 'month') {
        periodStart      = new Date(selectedYear, selectedMonth, 1);
        periodStart.setHours(0, 0, 0, 0);
        periodEnd        = new Date(selectedYear, selectedMonth + 1, 0);
        periodEnd.setHours(23, 59, 59, 999);
        effectiveTermKey = getTermForMonth(termStructure, selectedMonth, selectedYear);
      } else if (selectedTerm !== 'year') {
        effectiveTermKey = selectedTerm;
        if (termStructure?.termDates?.[selectedTerm]) {
          const td = termStructure.termDates[selectedTerm];
          const [sm, sd] = td.start.split('-').map(Number);
          const [em, ed] = td.end.split('-').map(Number);
          periodStart = new Date(selectedYear, sm - 1, sd);
          periodStart.setHours(0, 0, 0, 0);
          periodEnd   = new Date(selectedYear, em - 1, ed);
          periodEnd.setHours(23, 59, 59, 999);
        }
      }

      // ── Work out how many months are in each term for this school ──────────
      // 4-term school → 3 months per term  (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec)
      // 3-term school → 4 months per term
      const schoolTermType  = school.schoolFee?.termType || 4;
      const monthsPerTerm   = schoolTermType === 3 ? 4 : 3;

      const schoolRef    = doc(db, 'schools', selectedSchoolId);
      const studentsSnap = await getDocs(query(collection(db, 'students'), where('schoolRef', '==', schoolRef)));
      const today        = new Date(); today.setHours(0, 0, 0, 0);

      const processed = studentsSnap.docs.map(d => {
        const data         = d.data();
        const termPayments = data.termPayments || {};
        const isActive     = data.isActive !== false;
        let startDate = null;
        try {
          startDate = data.startDate?.toDate ? data.startDate.toDate() : new Date(data.startDate);
          startDate.setHours(0, 0, 0, 0);
        } catch { /* */ }
        let deactivatedDate = null;
        if (data.deactivationInfo?.deactivatedDate) {
          try { deactivatedDate = new Date(data.deactivationInfo.deactivatedDate); } catch { /* */ }
        }

        let attended = false;
        let feesPaid = 0;

        if (periodType === 'month') {
          attended = (startDate && startDate <= periodEnd) && (isActive || (deactivatedDate && deactivatedDate > periodStart));
          // Monthly fee = term fee paid ÷ months per term
          const termFeesPaid = effectiveTermKey ? (termPayments[effectiveTermKey] || 0) : 0;
          feesPaid = termFeesPaid / monthsPerTerm;
        } else if (selectedTerm === 'year') {
          attended = (startDate && startDate.getFullYear() <= selectedYear) && (isActive || (deactivatedDate && deactivatedDate.getFullYear() >= selectedYear));
          feesPaid = (termPayments.term1||0)+(termPayments.term2||0)+(termPayments.term3||0)+(termPayments.term4||0);
        } else {
          if (periodStart && periodEnd) {
            attended = (startDate && startDate <= periodEnd) && (today >= periodStart) && (isActive || (deactivatedDate && deactivatedDate > periodStart));
          } else { attended = isActive; }
          feesPaid = termPayments[selectedTerm] || 0;
        }

        return {
          id: d.id,
          studentName:   data.studentName || 'Unknown',
          accountNumber: data.accountNumber || 'N/A',
          isActive, attended, feesPaid,
          termFee: data.feeStructure?.standardFee || school.schoolFee?.termFee || 0,
          isPaying: feesPaid > 0,
        };
      });

      const attending = processed.filter(s => s.attended);
      const included  = (studentFilter === 'paying' ? attending.filter(s => s.isPaying) : attending);
      included.sort((a, b) => a.studentName.localeCompare(b.studentName));

      const totalFeesPaid = included.reduce((sum, s) => sum + s.feesPaid, 0);
      let commissionAmount = 0, commissionDescription = '';
      if (commission.type === 'fixed') {
        commissionAmount      = commission.value || 0;
        commissionDescription = 'Fixed amount: R' + commissionAmount.toFixed(2) + ' per period';
      } else if (commission.type === 'percentage') {
        commissionAmount      = totalFeesPaid * (commission.value || 0) / 100;
        commissionDescription = commission.value + '% of R' + totalFeesPaid.toFixed(2) + ' fees received = R' + commissionAmount.toFixed(2);
      } else {
        commissionDescription = 'No commission configured for this school.';
      }

      const monthNote = (periodType === 'month' && effectiveTermKey)
        ? 'Monthly fee = term fee \u00f7 ' + monthsPerTerm + ' months (this is a ' + schoolTermType + '-term school). '
          + 'Fees shown are the monthly portion of ' + effectiveTermKey.replace('term','Term ') + ' payments.'
        : (periodType === 'month' && !effectiveTermKey)
        ? 'This month falls outside any configured term period.'
        : null;

      setReportData({
        school, commission,
        students: included,
        totalAttending: attending.length,
        totalIncluded:  included.length,
        totalFeesPaid, commissionAmount, commissionDescription,
        periodLabel:    getPeriodLabel(),
        filterLabel:    studentFilter === 'paying' ? 'Paying Students Only' : 'All Attending Students',
        monthNote,
        monthsPerTerm,
        schoolTermType,
        generatedDate:  new Date().toLocaleDateString('en-ZA'),
      });
    } catch (err) {
      console.error('Error generating report:', err);
      alert('Error generating report. Please try again.');
    } finally {
      setLoadingReport(false);
    }
  };

  const exportToPDF = async () => {
    if (!reportData) return;
    const { school, students, totalFeesPaid, commissionAmount, commissionDescription,
            periodLabel, filterLabel, commission, monthNote, generatedDate } = reportData;

    const pdf   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const M     = 14;
    const BLUE  = [43, 75, 160];
    const GREY  = [60, 60, 60];
    const LGREY = [245, 246, 250];
    let y = 0;

    // Header banner
    pdf.setFillColor(...BLUE);
    pdf.rect(0, 0, pageW, 40, 'F');

    let logoLoaded = false;
    try {
      const img = new Image(); img.src = '/arty.png';
      await new Promise(res => { img.onload = () => { pdf.addImage(img,'PNG',M,7,24,24); logoLoaded=true; res(); }; img.onerror=()=>res(); setTimeout(res,2000); });
    } catch { /* */ }

    const tx = logoLoaded ? M + 28 : M;
    pdf.setTextColor(255,255,255);
    pdf.setFontSize(15); pdf.setFont(undefined,'bold');
    pdf.text('SCHOOL COMMISSION REPORT', tx, 17);
    pdf.setFontSize(8.5); pdf.setFont(undefined,'normal');
    pdf.text(franchiseeData.name || '', tx, 25);
    pdf.text('Generated: ' + generatedDate, tx, 32);
    y = 48;

    // Info boxes
    const boxY = y;
    [[school.schoolName, 'SCHOOL', M, 85],
     [periodLabel, 'PERIOD', M+89, 50],
     [filterLabel, 'STUDENTS', M+143, 53]
    ].forEach(([val, lbl, x, w]) => {
      pdf.setFillColor(...LGREY); pdf.roundedRect(x, boxY, w, 20, 2, 2, 'F');
      pdf.setTextColor(120,120,120); pdf.setFontSize(6.5); pdf.setFont(undefined,'bold');
      pdf.text(lbl, x+4, boxY+6);
      pdf.setTextColor(...BLUE); pdf.setFontSize(9); pdf.setFont(undefined,'bold');
      const lines = pdf.splitTextToSize(val, w-8);
      pdf.text(lines[0], x+4, boxY+14);
    });
    y = boxY + 26;

    // Commission badge
    const bc = commission.type !== 'none' ? [[232,245,233],[76,175,80]] : [[255,248,225],[255,193,7]];
    pdf.setFillColor(...bc[0]); pdf.setDrawColor(...bc[1]); pdf.setLineWidth(0.4);
    pdf.roundedRect(M, y, pageW-M*2, 11, 2, 2, 'FD');
    pdf.setTextColor(...GREY); pdf.setFontSize(8); pdf.setFont(undefined,'bold');
    pdf.text('Commission:', M+4, y+7.5);
    pdf.setFont(undefined,'normal');
    pdf.text(commissionDescription, M+28, y+7.5);
    y += 17;

    if (monthNote) {
      pdf.setFillColor(255,243,224); pdf.setDrawColor(255,152,0);
      pdf.roundedRect(M, y, pageW-M*2, 10, 2, 2, 'FD');
      pdf.setTextColor(100,60,0); pdf.setFontSize(7.5); pdf.setFont(undefined,'italic');
      pdf.text('Note: ' + monthNote, M+4, y+7);
      y += 16;
    }

    // Section title
    pdf.setTextColor(...GREY); pdf.setFontSize(10); pdf.setFont(undefined,'bold');
    pdf.text('Student Detail', M, y);
    y += 4;

    const rows = students.map((s, i) => [
      i+1, s.studentName, s.accountNumber,
      s.isActive ? 'Active' : 'Inactive',
      'R' + s.feesPaid.toFixed(2),
      commission.type === 'percentage' ? 'R' + (s.feesPaid*(commission.value||0)/100).toFixed(2) : '\u2014',
    ]);

    autoTable(pdf, {
      startY: y, margin: { left: M, right: M },
      head: [['#','Student Name','Account #','Status','Fees Paid','Commission Share']],
      body: rows,
      foot: [['','TOTALS','','','R'+totalFeesPaid.toFixed(2),'']],
      headStyles:  { fillColor: BLUE, textColor:[255,255,255], fontStyle:'bold', fontSize:8, cellPadding:3 },
      footStyles:  { fillColor:[230,235,255], textColor:GREY, fontStyle:'bold', fontSize:8.5 },
      bodyStyles:  { fontSize:8.5, cellPadding:3, textColor:GREY },
      alternateRowStyles: { fillColor: LGREY },
      columnStyles: {
        0:{ halign:'center', cellWidth:8 },
        1:{ cellWidth:58 },
        2:{ cellWidth:26 },
        3:{ cellWidth:20 },
        4:{ halign:'right', cellWidth:24 },
        5:{ halign:'right', cellWidth:28 },
      },
      didParseCell: (data) => {
        if (data.section==='body' && data.column.index===3) {
          data.cell.styles.textColor = data.cell.raw==='Active' ? [27,94,32] : [183,28,28];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    const ay = pdf.lastAutoTable.finalY + 6;

    // Commission summary
    pdf.setFillColor(...BLUE);
    pdf.roundedRect(M, ay, pageW-M*2, 28, 4, 4, 'F');
    pdf.setTextColor(255,255,255);
    pdf.setFontSize(8); pdf.setFont(undefined,'normal');
    pdf.text('Total Fees Received:', M+6, ay+10);
    pdf.setFontSize(11); pdf.setFont(undefined,'bold');
    pdf.text('R' + totalFeesPaid.toFixed(2), M+6, ay+20);

    pdf.setDrawColor(255,255,255); pdf.setLineWidth(0.3);
    pdf.line(pageW/2, ay+4, pageW/2, ay+24);

    pdf.setFontSize(8); pdf.setFont(undefined,'normal');
    pdf.setTextColor(255,220,120);
    pdf.text('COMMISSION DUE TO SCHOOL:', pageW/2+6, ay+10);
    pdf.setFontSize(14); pdf.setFont(undefined,'bold');
    pdf.setTextColor(255,255,255);
    pdf.text('R' + commissionAmount.toFixed(2), pageW/2+6, ay+22);

    // Footer
    pdf.setFontSize(7); pdf.setFont(undefined,'normal'); pdf.setTextColor(160,160,160);
    pdf.text('BABS \u2014 Beaux-Arts Bookkeeping System  |  ' + generatedDate + '  |  Confidential', pageW/2, pageH-8, { align:'center' });

    const safeName  = school.schoolName.replace(/\s+/g,'-');
    const safeLabel = getPeriodLabel().replace(/\s+/g,'-');
    pdf.save('commission-' + safeName + '-' + safeLabel + '-' + new Date().toISOString().split('T')[0] + '.pdf');
  };

  const exportToCSV = () => {
    if (!reportData) return;
    const { school, students, totalFeesPaid, commissionAmount, commissionDescription, periodLabel, filterLabel } = reportData;
    const lines = [
      '"School Commission Report"',
      '"School:","' + school.schoolName + '"',
      '"Period:","' + periodLabel + '"',
      '"Students:","' + filterLabel + '"',
      '"Commission:","' + commissionDescription + '"',
      '',
      '"#","Student Name","Account #","Status","Fees Paid (R)","Commission Share"',
      ...students.map((s, i) => {
        const share = reportData.commission.type === 'percentage'
          ? 'R' + (s.feesPaid*(reportData.commission.value||0)/100).toFixed(2) : '\u2014';
        return '"' + (i+1) + '","' + s.studentName + '","' + s.accountNumber + '","' + (s.isActive?'Active':'Inactive') + '","' + s.feesPaid.toFixed(2) + '","' + share + '"';
      }),
      '',
      '"","TOTAL FEES RECEIVED","","","' + totalFeesPaid.toFixed(2) + '",""',
      '"","COMMISSION DUE","","","' + commissionAmount.toFixed(2) + '",""',
    ];
    const blob = new Blob([lines.join('\n')], { type:'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'commission-' + school.schoolName.replace(/\s+/g,'-') + '-' + periodLabel.replace(/\s+/g,'-') + '-' + new Date().toISOString().split('T')[0] + '.csv';
    a.click(); URL.revokeObjectURL(url);
  };

  if (loadingSchools) return <div className="scr-loading">Loading schools\u2026</div>;

  const selectedSchool = schools.find(s => s.id === selectedSchoolId);
  const hasCommission  = selectedSchool?.commission?.type && selectedSchool.commission.type !== 'none';

  return (
    <div className="scr-container">
      <div className="scr-header">
        <h1>&#127968; School Commission Report</h1>
        <p className="scr-subtitle">Calculate commission due to a school for any term or monthly period.</p>
      </div>

      <div className="scr-filters-card">
        <div className="scr-filter-row">
          <div className="scr-filter-group scr-wide">
            <label>Select School</label>
            <select value={selectedSchoolId} onChange={e => { setSelectedSchoolId(e.target.value); setReportData(null); }} size={Math.min(schools.length,6)} className="scr-select-list">
              {schools.map(s => <option key={s.id} value={s.id}>{s.schoolName}</option>)}
            </select>
            {schools.length === 0 && <p className="scr-note">No active schools found.</p>}
          </div>

          <div className="scr-filter-group">
            <label>Period Type</label>
            <div className="scr-toggle-row">
              <button className={'scr-toggle-btn' + (periodType==='term'?' active':'')} onClick={() => { setPeriodType('term'); setReportData(null); }}>&#128197; By Term</button>
              <button className={'scr-toggle-btn' + (periodType==='month'?' active':'')} onClick={() => { setPeriodType('month'); setReportData(null); }}>&#128467; By Month</button>
            </div>

            {periodType === 'term' && (
              <>
                <label style={{marginTop:'14px'}}>Select Term</label>
                <select value={selectedTerm} onChange={e => { setSelectedTerm(e.target.value); setReportData(null); }} className="scr-select">
                  {TERM_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </>
            )}

            {periodType === 'month' && (
              <>
                <label style={{marginTop:'14px'}}>Month &amp; Year</label>
                <div style={{display:'flex',gap:'8px'}}>
                  <select value={selectedMonth} onChange={e => { setSelectedMonth(parseInt(e.target.value)); setReportData(null); }} className="scr-select" style={{flex:2}}>
                    {MONTH_NAMES.map((m,i) => <option key={i} value={i}>{m}</option>)}
                  </select>
                  <select value={selectedYear} onChange={e => { setSelectedYear(parseInt(e.target.value)); setReportData(null); }} className="scr-select" style={{flex:1}}>
                    {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}

            <label style={{marginTop:'14px'}}>Include Students</label>
            <select value={studentFilter} onChange={e => { setStudentFilter(e.target.value); setReportData(null); }} className="scr-select">
              <option value="all">All Attending Students</option>
              <option value="paying">Paying Students Only</option>
            </select>
          </div>
        </div>

        {selectedSchool && (
          <div className={'scr-commission-badge ' + (hasCommission ? 'badge-active' : 'badge-none')}>
            <span className="badge-icon">{hasCommission ? '&#128176;' : 'ℹ️'}</span>
            <span>
              {hasCommission
                ? <><strong>Commission configured:</strong> {selectedSchool.commission.type==='percentage' ? selectedSchool.commission.value+'% of fees collected' : 'R'+(selectedSchool.commission.value||0).toFixed(2)+' fixed per period'}</>
                : <>No commission configured for this school. Add it via <strong>Edit School</strong>.</>}
            </span>
          </div>
        )}

        <button className="scr-generate-btn" onClick={generateReport} disabled={loadingReport || !selectedSchoolId}>
          {loadingReport ? '⏳ Generating…' : '📊 Generate Report'}
        </button>
      </div>

      {reportData && (
        <div className="scr-results">
          <div className="scr-summary-row">
            <div className="scr-summary-card card-blue"><div className="summary-value">{reportData.totalAttending}</div><div className="summary-label">Students Attended</div></div>
            <div className="scr-summary-card card-green"><div className="summary-value">{reportData.totalIncluded}</div><div className="summary-label">{studentFilter==='paying'?'Paying Students':'Students Included'}</div></div>
            <div className="scr-summary-card card-teal"><div className="summary-value">R{reportData.totalFeesPaid.toFixed(2)}</div><div className="summary-label">Total Fees Received</div></div>
            <div className="scr-summary-card card-orange commission-highlight"><div className="summary-value">R{reportData.commissionAmount.toFixed(2)}</div><div className="summary-label">Commission Due</div></div>
          </div>

          {reportData.monthNote && <div className="scr-month-note">ℹ️ {reportData.monthNote}</div>}

          <div className="scr-commission-detail">
            <strong>📌 Commission:</strong> {reportData.commissionDescription}
          </div>

          <div className="scr-report-meta">
            <span>🏫 <strong>{reportData.school.schoolName}</strong></span>
            <span>📅 <strong>{reportData.periodLabel}</strong></span>
            <span>👥 <strong>{reportData.filterLabel}</strong></span>
            <div className="scr-action-btns">
              <button className="scr-export-btn scr-pdf-btn" onClick={exportToPDF}>📄 Download PDF</button>
              <button className="scr-export-btn scr-csv-btn" onClick={exportToCSV}>⬇ Download CSV</button>
            </div>
          </div>

          {reportData.students.length === 0 ? (
            <div className="scr-empty">No students match the selected filters for this period.</div>
          ) : (
            <div className="scr-table-wrapper">
              <table className="scr-table">
                <thead>
                  <tr><th>#</th><th>Student Name</th><th>Account #</th><th>Status</th><th>Fees Paid (R)</th><th>Commission Share</th></tr>
                </thead>
                <tbody>
                  {reportData.students.map((s, idx) => (
                    <tr key={s.id} className={idx%2===0?'row-even':'row-odd'}>
                      <td className="td-num">{idx+1}</td>
                      <td className="td-name">{s.studentName}</td>
                      <td className="td-acc">{s.accountNumber}</td>
                      <td><span className={'status-badge '+(s.isActive?'status-active':'status-inactive')}>{s.isActive?'Active':'Inactive'}</span></td>
                      <td className="td-money">R{s.feesPaid.toFixed(2)}</td>
                      <td className="td-basis">{reportData.commission.type==='percentage' ? 'R'+(s.feesPaid*(reportData.commission.value||0)/100).toFixed(2) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="tfoot-totals"><td colSpan="4"><strong>TOTALS</strong></td><td className="td-money"><strong>R{reportData.totalFeesPaid.toFixed(2)}</strong></td><td></td></tr>
                  <tr className="tfoot-commission"><td colSpan="4"><strong>COMMISSION DUE TO SCHOOL</strong></td><td className="td-money commission-total" colSpan="2"><strong>R{reportData.commissionAmount.toFixed(2)}</strong></td></tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SchoolCommissionReport;